import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ChevronDown, ChevronRight, MessageSquare, Mail, Phone, Send, HelpCircle } from 'lucide-react';
import { faqAPI, settingsAPI, heroAPI } from '../utils/api';
import Header from '../components/Header';
import Footer from '../components/Footer';

const SupportPage = () => {
  const [faq, setFaq] = useState([]);
  const [settings, setSettings] = useState(null);
  const [heroContent, setHeroContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [faqRes, settingsRes, heroRes] = await Promise.all([
          faqAPI.getAll(),
          settingsAPI.get(),
          heroAPI.get()
        ]);

        setFaq(faqRes.data);
        setSettings(settingsRes.data);
        setHeroContent(heroRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleExpanded = (faqId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(faqId)) {
      newExpanded.delete(faqId);
    } else {
      newExpanded.add(faqId);
    }
    setExpandedItems(newExpanded);
  };

  if (loading) {
    return (
      <div className="public-page min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-xl">Загружается...</div>
      </div>
    );
  }

  return (
    <div className="public-page min-h-screen bg-gray-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Поддержка и помощь
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Найдите ответы на вопросы о работе с AI-переводчиком @lngaua_bot или свяжитесь с нами напрямую
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="pb-12">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
          >
            {/* Telegram Bot */}
            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader className="text-center pb-4">
                <div className="w-16 h-16 bg-blue-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <MessageSquare className="h-8 w-8 text-blue-400" />
                </div>
                <CardTitle className="text-white">Telegram бот</CardTitle>
                <CardDescription className="text-gray-400">
                  Прямая связь с ботом
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center">
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => window.open(heroContent?.bot_link || "https://t.me/lngaua_bot", "_blank")}
                  data-testid="contact-bot-btn"
                >
                  Открыть @lngaua_bot
                </Button>
              </CardContent>
            </Card>

            {/* Support Chat */}
            {settings?.support_chat && (
              <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-green-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Send className="h-8 w-8 text-green-400" />
                  </div>
                  <CardTitle className="text-white">Чат поддержки</CardTitle>
                  <CardDescription className="text-gray-400">
                    Техническая поддержка
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700"
                    onClick={() => window.open(settings.support_chat, "_blank")}
                    data-testid="contact-support-btn"
                  >
                    Написать в поддержку
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Email */}
            {settings?.contact_email && (
              <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-purple-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-purple-400" />
                  </div>
                  <CardTitle className="text-white">Email</CardTitle>
                  <CardDescription className="text-gray-400">
                    Электронная почта
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Button 
                    variant="outline"
                    className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                    onClick={() => window.open(`mailto:${settings.contact_email}`, "_blank")}
                    data-testid="contact-email-btn"
                  >
                    {settings.contact_email}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Telegram Channel */}
            {settings?.telegram_channel && (
              <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
                <CardHeader className="text-center pb-4">
                  <div className="w-16 h-16 bg-indigo-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Send className="h-8 w-8 text-indigo-400" />
                  </div>
                  <CardTitle className="text-white">Telegram канал</CardTitle>
                  <CardDescription className="text-gray-400">
                    Новости и обновления
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-center">
                  <Button 
                    variant="outline"
                    className="w-full border-gray-600 text-gray-300 hover:bg-gray-800"
                    onClick={() => window.open(settings.telegram_channel, "_blank")}
                    data-testid="contact-channel-btn"
                  >
                    Подписаться
                  </Button>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      {faq.length > 0 && (
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">Частые вопросы</h2>
              <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                Ответы на популярные вопросы о работе с AI-переводчиком
              </p>
            </motion.div>

            <div className="max-w-4xl mx-auto space-y-4">
              {faq.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
                    <CardHeader 
                      className="cursor-pointer"
                      onClick={() => toggleExpanded(item.id)}
                    >
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-white text-lg pr-4">{item.question}</CardTitle>
                        <div className="flex-shrink-0">
                          {expandedItems.has(item.id) ? (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    
                    {expandedItems.has(item.id) && (
                      <CardContent className="pt-0">
                        <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                          {item.answer}
                        </div>
                      </CardContent>
                    )}
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick Start Guide */}
      <section className="py-20 bg-gray-800/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Быстрый старт</h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Как начать использовать AI-переводчик за несколько простых шагов
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {[
              {
                step: 1,
                title: "Запустите бота",
                description: "Перейдите в Telegram и отправьте команду /start боту @lngaua_bot"
              },
              {
                step: 2,
                title: "Выберите языки",
                description: "Настройте, с какого на какой язык будет происходить перевод"
              },
              {
                step: 3,
                title: "Получите 15 бесплатных сообщений",
                description: "Протестируйте качество переводов с бесплатными сообщениями"
              },
              {
                step: 4,
                title: "Купите пакет сообщений",
                description: "Выберите подходящий тариф и продолжайте использовать бота"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex items-center space-x-6 mb-8"
              >
                <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {item.step}
                </div>
                <div className="flex-1">
                  <Card className="bg-gray-800 border-gray-700">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-semibold text-white mb-2">{item.title}</h3>
                      <p className="text-gray-300">{item.description}</p>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">Остались вопросы?</h2>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Свяжитесь с нами любым удобным способом, и мы обязательно поможем
            </p>
            <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
              <Button 
                size="lg"
                className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-4"
                onClick={() => window.open(heroContent?.bot_link || "https://t.me/lngaua_bot", "_blank")}
                data-testid="help-contact-btn"
              >
                <MessageSquare className="mr-2 h-5 w-5" />
                Написать в бота
              </Button>
              {settings?.support_chat && (
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-gray-600 text-gray-300 hover:bg-gray-800 text-lg px-8 py-4"
                  onClick={() => window.open(settings.support_chat, "_blank")}
                >
                  <HelpCircle className="mr-2 h-5 w-5" />
                  Техподдержка
                </Button>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
